import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPublicClient, hexToString, http, keccak256, parseAbiItem, toHex, type Address, type Hex } from 'viem'
import { CHAINS, LSP0_ABI, PERMISSION_CONTROL } from '../constants'
import { PERMISSION_CONTROL_ABI } from '../contracts/permissionControl'
import {
  fetchLogsPreferUnchunked,
  latestMintPerSeries,
  mergeMintedLogs,
  readTemplateMintCache,
  TEMPLATE_CACHE_VERSION,
  writeTemplateMintCache,
  type MintedTemplateLog,
} from '../lib/templateDiscovery'
import {
  convertIpfsUrl,
  normalizeTemplateMetadataFromJson,
  parseTemplateMetadataFromUri,
  type TemplateMetadata,
} from '../utils'

export type TemplateId = Hex // bytes32

export type RegistryTemplate = {
  publisherUP: Address
  templateId: TemplateId
  version: number
  chainId: bigint
  permissions: bigint
  allowedCalls: Hex
  allowedERC725YDataKeys: Hex
  metadataURI: string
  deprecated: boolean
  /** LSP8 token id for this template (minted on publish). */
  tokenId: Hex
  publishedBlockNumber?: bigint
}

export type { TemplateMetadata } from '../utils'

type Status = 'idle' | 'loading' | 'ready' | 'error'

type TemplateData = {
  publisherUP: Address
  templateId: TemplateId
  version: number
  chainId: bigint
  permissions: bigint
  allowedCalls: Hex
  allowedERC725YDataKeys: Hex
  metadataURI: string
  deprecated: boolean
}

function decodeTemplateData(raw: unknown): TemplateData | null {
  if (!raw) return null
  if (Array.isArray(raw) && raw.length >= 9) {
    const [
      publisherUP,
      templateId,
      version,
      chainId,
      permissions,
      allowedCalls,
      allowedERC725YDataKeys,
      metadataURI,
      deprecated,
    ] = raw as [
      Address,
      TemplateId,
      number | bigint,
      bigint,
      bigint,
      Hex,
      Hex,
      string,
      boolean,
    ]
    return {
      publisherUP,
      templateId,
      version: Number(version),
      chainId,
      permissions,
      allowedCalls,
      allowedERC725YDataKeys,
      metadataURI,
      deprecated,
    }
  }
  if (typeof raw === 'object' && raw !== null && 'publisherUP' in raw && 'deprecated' in raw) {
    const t = raw as TemplateData
    return { ...t, version: Number(t.version) }
  }
  return null
}

const TEMPLATE_MINTED_EVENT = parseAbiItem(
  'event TemplateMinted(address indexed publisherUP, bytes32 indexed templateId, uint32 indexed version, bytes32 tokenId, uint256 chainId, uint256 permissions, bytes allowedCalls, bytes allowedERC725YDataKeys, string metadataURI)'
)

/**
 * PermissionControl is LSP8, but not enumerable: there is no tokenByIndex / all-token list.
 * Marketplace discovery is: totalSupply (empty check) + TemplateMinted logs (token ids) +
 * getTemplateByTokenId (current deprecated/metadata). tokenIdsOf(owner) only lists one UP's NFTs.
 */
export function templateIdFromSlug(slug: string): TemplateId {
  return keccak256(toHex(slug)) as TemplateId
}

export function useTemplateRegistry({ enabled = false }: { enabled?: boolean } = {}) {
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState<string | null>(null)
  const [templates, setTemplates] = useState<RegistryTemplate[]>([])

  const metadataCache = useRef<Map<string, TemplateMetadata | null>>(new Map())

  const registry = PERMISSION_CONTROL

  const publicClient = useMemo(() => {
    const rpcUrl = CHAINS.lukso.rpcUrls.default.http[0]
    return createPublicClient({
      chain: CHAINS.lukso as never,
      transport: http(rpcUrl),
    })
  }, [])

  const fetchMetadata = useCallback(async (publisherUP: Address, metadataURI: string): Promise<TemplateMetadata | null> => {
    if (!metadataURI) return null
    const cacheKey = `${publisherUP}-${metadataURI}`
    if (metadataCache.current.has(cacheKey)) return metadataCache.current.get(cacheKey) ?? null

    try {
      const inline = parseTemplateMetadataFromUri(metadataURI)
      if (inline !== null && typeof inline === 'object') {
        const meta = normalizeTemplateMetadataFromJson(inline)
        if (meta) {
          metadataCache.current.set(cacheKey, meta)
          return meta
        }
      }

      if (metadataURI.startsWith('erc725y:')) {
        const key = metadataURI.slice('erc725y:'.length).trim()
        if (!/^0x[a-fA-F0-9]{64}$/.test(key)) {
          metadataCache.current.set(cacheKey, null)
          return null
        }
        const raw = await publicClient.readContract({
          address: publisherUP,
          abi: LSP0_ABI,
          functionName: 'getData',
          args: [key as Hex],
        }) as Hex

        if (!raw || raw === '0x') {
          metadataCache.current.set(cacheKey, null)
          return null
        }

        const text = hexToString(raw).replace(/\u0000+$/g, '')
        const json = JSON.parse(text)
        const meta = normalizeTemplateMetadataFromJson(json)
        if (meta) {
          metadataCache.current.set(cacheKey, meta)
          return meta
        }
        metadataCache.current.set(cacheKey, null)
        return null
      }

      const url = convertIpfsUrl(metadataURI)
      const res = await fetch(url)
      if (!res.ok) {
        metadataCache.current.set(cacheKey, null)
        return null
      }
      const json = await res.json()
      const meta = normalizeTemplateMetadataFromJson(json)
      if (meta) {
        metadataCache.current.set(cacheKey, meta)
        return meta
      }
      metadataCache.current.set(cacheKey, null)
      return null
    } catch {
      metadataCache.current.set(cacheKey, null)
      return null
    }
  }, [publicClient])

  const refresh = useCallback(async () => {
    setStatus('loading')
    setError(null)

    try {
      const latest = await publicClient.getBlockNumber()
      const supply = await publicClient.readContract({
        address: registry.address,
        abi: PERMISSION_CONTROL_ABI,
        functionName: 'totalSupply',
      }) as bigint

      const cached = readTemplateMintCache(registry.address)
      let minted = cached?.minted ?? []

      if (supply === 0n && minted.length === 0) {
        setTemplates([])
        writeTemplateMintCache({
          version: TEMPLATE_CACHE_VERSION,
          address: registry.address,
          lastBlock: latest.toString(),
          minted: [],
        })
        setStatus('ready')
        return
      }

      const cachedLast = cached ? BigInt(cached.lastBlock) : 0n
      const fromBlock = minted.length > 0 && cachedLast >= registry.deploymentBlock
        ? cachedLast + 1n
        : registry.deploymentBlock

      if (fromBlock <= latest) {
        const logs = await fetchLogsPreferUnchunked(
          (from, to) => publicClient.getLogs({
            address: registry.address,
            event: TEMPLATE_MINTED_EVENT,
            fromBlock: from,
            toBlock: to,
          }),
          fromBlock,
          latest,
        )

        const incoming: MintedTemplateLog[] = logs.map((log) => {
          const args = log.args as unknown as {
            publisherUP: Address
            templateId: TemplateId
            version: number
            tokenId: Hex
            chainId: bigint
            permissions: bigint
            allowedCalls: Hex
            allowedERC725YDataKeys: Hex
            metadataURI: string
          }
          return {
            ...args,
            blockNumber: log.blockNumber ?? undefined,
          }
        })
        minted = mergeMintedLogs(minted, incoming)
      }

      writeTemplateMintCache({
        version: TEMPLATE_CACHE_VERSION,
        address: registry.address,
        lastBlock: latest.toString(),
        minted,
      })

      const latestMints = latestMintPerSeries(minted)
      const latestTemplates: RegistryTemplate[] = []

      const reads = await Promise.all(latestMints.map(async (mint) => {
        try {
          const raw = await publicClient.readContract({
            address: registry.address,
            abi: PERMISSION_CONTROL_ABI,
            functionName: 'getTemplateByTokenId',
            args: [mint.tokenId],
          })
          return { mint, template: decodeTemplateData(raw) }
        } catch {
          return { mint, template: null }
        }
      }))

      for (const { mint, template } of reads) {
        if (!template || template.deprecated) continue
        latestTemplates.push({
          publisherUP: template.publisherUP,
          templateId: template.templateId,
          version: Number(template.version),
          chainId: template.chainId,
          permissions: template.permissions,
          allowedCalls: template.allowedCalls,
          allowedERC725YDataKeys: template.allowedERC725YDataKeys,
          metadataURI: template.metadataURI,
          deprecated: template.deprecated,
          tokenId: mint.tokenId,
          publishedBlockNumber: mint.blockNumber,
        })
      }

      await Promise.all(latestTemplates.map(t => fetchMetadata(t.publisherUP, t.metadataURI)))

      latestTemplates.sort((a, b) => {
        const ab = a.publishedBlockNumber ?? 0n
        const bb = b.publishedBlockNumber ?? 0n
        if (ab === bb) return b.version - a.version
        return bb > ab ? 1 : -1
      })

      setTemplates(latestTemplates)
      setStatus('ready')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load registry templates'
      setError(msg)
      setStatus('error')
    }
  }, [publicClient, registry.address, registry.deploymentBlock, fetchMetadata])

  useEffect(() => {
    if (!enabled) return
    if (status === 'ready' || status === 'loading') return
    void refresh()
  }, [enabled, refresh, status])

  const getMetadataForTemplate = useCallback((t: RegistryTemplate): TemplateMetadata | null => {
    return metadataCache.current.get(`${t.publisherUP}-${t.metadataURI}`) ?? null
  }, [])

  return {
    status,
    error,
    templates,
    refresh,
    getMetadataForTemplate,
    registry,
  }
}
